import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from 'common/components/elements/buttons/primary';
import cx from 'classnames';
import { Tooltip } from 'react-tooltip';
import { createPortal } from 'react-dom';
import ConfirmModal from 'common/components/modules/modal-wrapper';
import Icon from 'common/components/elements/icons';
import { MENU_SEGMENT_KEY, CREATE_PAGE_SEGMENT_KEY, SECTION_SEGMENT_KEY, GLOBA_SEGMENT_KEY, PAGE_SEGMENT_KEY, getSegmentStates } from 'admin/views/site-designer/data';
import {
   sortableHandle,
   sortableElement,
} from 'react-sortable-hoc';
import TruncateText from "common/components/elements/truncate-text";

const leftSideStates = getSegmentStates();

const DotsOptions = ({
   tooltipId,
   onClickOptions,
   options,
   showRevertButton,
   onClickRevert = () => {},
}) => {
   const [isOpentSettinsTooltip, setOpenSettinsTooltip] = useState(false);

   useEffect(() => {
      window.addEventListener('click', closeOnClickOutside, true);
      return () => {
         window.removeEventListener('click', closeOnClickOutside, true);
      };
   // eslint-disable-next-line
   }, [isOpentSettinsTooltip]);

   function closeOnClickOutside(e) {
      if(isOpentSettinsTooltip && !e.target.closest(`#${ tooltipId }`) && !e.target.closest(`#button_${ tooltipId }`)) {
         setOpenSettinsTooltip(false)
      }

   }

   return (
      <div
         data-tooltip-id='ams-top-tooltip'
         data-tooltip-content={ showRevertButton ? 'Revert' : null }
         id={ `button_${ tooltipId }` }
         className='w-5 h-5 flex items-center justify-center'>
         <ActionButton
            iconName={ showRevertButton ? 'revert' : 'options-vertical' }
            size='lg'
            onClick={ (e) => {
               e.stopPropagation();
               e.preventDefault();
               if(showRevertButton){
                  onClickRevert();
                  return;
               }
               setOpenSettinsTooltip(!isOpentSettinsTooltip)
            } }
            data-tooltip-id={ tooltipId }
         />
         {
            createPortal(
               <Tooltip
                  openOnClick
                  role='dialog'
                  variant='light'
                  id={ tooltipId }
                  place='bottom'
                  clickable
                  isOpen={ isOpentSettinsTooltip }
                  offset={ 4 }
                  noArrow
                  style={ {
                     padding: 0,
                     borderRadius: '8px',
                  } }
                  className='shadow-dropdown dark:shadow-dark border dark:border-divider-dark border-divider min-w-[200px] z-60'
               >
                  {
                     options.map((option) => {
                        return (
                           <OptionsButton
                              iconName={ option.icon }
                              fontColor={ option.color }
                              textColor={ option.textColor }
                              text={ option.title }
                              onClick={ e => {
                                 e.stopPropagation();
                                 e.preventDefault();
                                 onClickOptions(option.value);
                                 setOpenSettinsTooltip(false);
                              } }
                              disabled={ option.disabled }
                              tootltip={ option.disabled && option.disabledTooltipText ? {
                                 'data-tooltip-content': option.disabledTooltipText,
                                 'data-tooltip-id': 'ams-top-tooltip',
                              } : {} }
                           />
                        )

                     })
                  }

               </Tooltip >,
               document.getElementById('tooltips')
            )
         }
      </div>
   )
}

const HeaderAddAndReorderButton = ({
   addButtonText,
   isOpenReorder,
   onClickReorder,
   onClickDone,
   onClickCancel,
   onClickAddNew,
   disabledDoneButton,
   showAddButton = true,
}) => {
   return (
      <div className='flex w-full justify-between'>
         {
            showAddButton && (
               <OptionsButton
                  fontColor='action'
                  iconName='add'
                  text={ addButtonText }
                  disabled={ isOpenReorder }
                  onClick={ onClickAddNew }
                  textSize='small'
                  padding='0 py-[10px]'
               />
            )
         }
         {
            isOpenReorder && (
               <div className='flex gap-4'>
                  <OptionsButton
                     fontColor='secondary'
                     text='Cancel'
                     onClick={ onClickCancel }
                     textSize='small'
                     padding='0 py-[10px]'
                  />
                  <OptionsButton
                     fontColor='action'
                     text='Done'
                     onClick={ onClickDone }
                     disabled={ disabledDoneButton }
                     textSize='small'
                     padding='0 py-[10px]'
                  />
               </div>
            )
         }
         {
            !isOpenReorder && (
               <OptionsButton
                  fontColor='action'
                  iconName='drag'
                  text='Reorder'
                  onClick={ onClickReorder }
                  textSize='small'
                  padding='0 py-[10px]'
               />
            )
         }
      </div>
   )
}

const OptionsButton = ({
   iconName,
   text,
   fontColor,
   onClick,
   disabled,
   textSize = 'base',
   padding = '0 py-[10px] px-4',
   textColor,
   tootltip = {},
}) => {
   return (
      <div
         { ...tootltip }
         className='h-10 justify-items-start'
      >
         <Button
            classNames='w-full h-full flex items-center ju'
            padding={ padding }
            iconName={ iconName }
            text={ text }
            backgroundColor='transparent'
            fontIconColor={ fontColor }
            textColor={ textColor || fontColor }
            onClick={ onClick }
            iconPosition='left'
            textMarginX='0 ml-2'
            textSize={ textSize }
            alignment='end'
            disabled={ disabled }
         />
      </div>
   )
}

const ActionButton = ({
   iconName,
   size,
   primaryColor,
   className,
   onClick,
   disabled,
   buttonState,
   fontColor,
   ...rest
}) => {
   return (
      <div
         { ...rest }
         className={ cx({
            [`${ className }`]: true,
            'h-9 w-9 min-w-[36px]': 'xl' === size,
            'h-5 w-5 min-w-[20px]': 'lg' === size,
         }) }
      >
         <Button
            classNames='w-full h-full flex items-center'
            padding='0'
            iconName={ iconName }
            backgroundColor='transparent'
            fontIconColor={ fontColor || 'secondary' }
            textColor={ fontColor || 'secondary' }
            primaryColor={ primaryColor }
            onClick={ onClick }
            disabled={ disabled }
            textSize='small'
            borderRadius='large'
            { ...buttonState }
         />
      </div>
   )
}
const ConfirmationModal = ({
   onClose,
   isMobile,
   disabled,
   ...rest
}) => {
   return (
      <ConfirmModal
         type='confirm'
         { ...rest }
         isCloseAction
         buttonClassName='mt-8 !text-sm'
         isMobile={ isMobile }
         cancelButtonAction={ onClose }
         onCloseModal={ onClose }
         disabled={ disabled }
      />
   )
}

const Header = ({
   title,
   type,
   goToBack,
   stepActiveState,
   onSetStep,
   onSaveDraft,
   disabledSaveAsDraft,
   description,
}) => {
   return (
      <>
         <div className='flex flex-col gap-3'>
            {
               MENU_SEGMENT_KEY !== type && (
                  <div className='flex justify-between'>
                     <ActionButton
                        iconName='arrow-left'
                        onClick={ goToBack }
                        size={ null }
                        fontColor='secondary'
                        buttonState={ {
                           text: 'Back',
                           iconPosition: 'left',
                        } }
                     />

                     <div className='flex gap-3'>
                        <ActionButton
                           disabled={ !stepActiveState.undo }
                           onClick={ () => onSetStep('undo') }
                           iconName='revert'
                        />
                        <ActionButton
                           disabled={ !stepActiveState.redo }
                           onClick={ () => onSetStep('redo') }
                           iconName='redo'
                        />
                        <ActionButton
                           onClick={ onSaveDraft }
                           buttonState={ {
                              text: 'Save as draft',
                              backgroundColor: 'action',
                           } }
                           size={ null }
                           primaryColor
                           fontColor='secondary'
                           disabled={ disabledSaveAsDraft }
                        />
                     </div>

                  </div>
               )
            }
            <div className='flex flex-col gap-1'>
               <span className='panel-header text-major'>{title}</span>
               {
                  description && (
                     <span className='panel-description text-secondary'>{description}</span>
                  )
               }

            </div>
         </div>
      </>
   )
}

const LeftSideTabItem = ({
   text,
   iconName,
   checked,
   onClick,
   name,
}) => {
   return (
      <div
         role='presentation'
         onClick={ () => {
            if(!checked){
               onClick(name)
            }
         } }
         className={ cx({
            'w-full rounded-lg flex justify-center items-center cursor-pointer relative h-10 gap-2 site-builder-transition bg-grey-main': true,
            'bg-hover': checked,
         }) }
      >
         <div className='h-5 w-5 flex items-center justify-center'>
            <Icon
               name={ iconName }
               color={ checked ? 'action' : 'secondary' }
               className='site-builder-transition'
            />
         </div>
         <span
            className={ cx({
               'text-sm leading-[18px] font-medium select-none site-builder-transition': true,
               'text-action dark:text-action-dark': checked,
               'text-secondary dark:text-secondary-dark': !checked,
            }) }
         >{text}</span>
      </div>
   )
}

const LeftSideTab = ({
   activeTab,
   onClick,
}) => {
   return (
      <div
         className='flex w-full bg-grey-main rounded-lg p-[6px] gap-2'>

         <LeftSideTabItem
            iconName='colors'
            text='Page design'
            name='design'
            checked={ activeTab === 'design' }
            onClick={ onClick }
         />
         <LeftSideTabItem
            iconName='lock'
            text='Access settings'
            name='access_settings'
            checked={ activeTab === 'access_settings' }
            onClick={ onClick }
         />
      </div>
   )
}
const DragHandle = sortableHandle(() => (
   <div className='flex w-5 h-5 items-center drag-cursor'>
      <Icon name='drag'
         darkColor='secondary-dark'
         color='secondary'
         size='xl' />
   </div>
));

const LeftSideItemSortableItem = sortableElement(({ reorder }) => {
   return (
      <LeftSideItem
         item={ reorder }
         showDragIcon
         itemIndex={ reorder.id }
      />
   )
}
);
const LeftSideItem = ({
   leftSideType,
   className,
   withoutLeftIcon,
   onClick,
   canAdd,
   isAdded,
   dataTooltipContent,
   canUpdateOnlyVisibility,
   showDragIcon,
   itemIndex,
   onClickDelete,
   onToggleVisibility,
   onRestore,
   item,
}) => {
   const [isOpentSettinsTooltip, setOpenSettinsTooltip] = useState(false);
   const [confirmModalButtonDisabled, setConfirmModalButtonDisabled] = useState(false);
   const [showConfirmModal, setShowConfirmModal] = useState({
      isOpen: false,
      modalState: {},
   });

   const optionButtonClick = (e, bool = true) => {
      e.stopPropagation();
      e.preventDefault();
      if(bool){
         setOpenSettinsTooltip(false);
      }
   }


   function closeOnClickOutside(e) {
      if(isOpentSettinsTooltip && (!e.target.closest(`#left-item-${ itemIndex }-settings-button`) || !e.target.closest(`#left-item-${ itemIndex }-settings-tooltip`))) {
         setOpenSettinsTooltip(false)
      }
   }
   const onCloseConfirmModal = () => {
      setConfirmModalButtonDisabled(false);
      setShowConfirmModal({
         isOpen: false,
         buttonFetching: false,
         modalState: {},
      })
   }

   const onClickOptions = (e, type) => {
      const MODAL_STATE = {
         delete: {
            modal: {
               title: 'Delete?',
               buttonText: 'Yes',
            },
            action: callback => onClickDelete(callback),
         },
         revert: {
            modal: {
               title: 'Revert?',
               buttonText: 'Yes',
            },
            action: callback => onRestore(callback),
         },
         visibility: {
            modal: {
               title: item.is_active ? 'Hide?' : 'Show?',
               buttonText: 'Yes',
            },
            action: callback => onToggleVisibility(callback),
         },
      }
      const state = MODAL_STATE?.[type] || {};
      const stateData = state?.modal || {};
      setShowConfirmModal({
         isOpen: true,
         modalState: {
            ...stateData,
            action: () => {
               if(state?.action){
                  setConfirmModalButtonDisabled(true);
                  state.action(() => {
                     onCloseConfirmModal();
                  });
               } else {
                  onCloseConfirmModal();
               }
            },
         },
      })
      optionButtonClick(e);
   }

   useEffect(() => {
      window.addEventListener('click', closeOnClickOutside, true);
      return () => {
         window.removeEventListener('click', closeOnClickOutside, true);
      };
   // eslint-disable-next-line
   }, [isOpentSettinsTooltip]);


   const getSectionState = () => {
      if(leftSideType !== MENU_SEGMENT_KEY && leftSideStates?.[item?.type]){
         return {
            ...leftSideStates?.[item.type],
            ...item,
         }
      }
      return item;

   }
   const state = getSectionState() || {};
   return (
      <>
         {
            showConfirmModal?.isOpen && (
               <ConfirmationModal
                  onClose={ onCloseConfirmModal }
                  { ...showConfirmModal.modalState }
                  disabled={ confirmModalButtonDisabled }
               />
            )
         }
         <div
            data-tooltip-id='ams-top-tooltip'
            data-tooltip-content={ dataTooltipContent || '' }
            role='presentation'
            onClick={ _ => {
               if(Boolean(state.is_deleted)  || showDragIcon || withoutLeftIcon) {
                  return;
               }
               onClick()
            }  }
            id={ `left-item-${ itemIndex }-settings-button` }
            className={ cx({
               [`flex justify-between gap-2 px-4 py-[10px]  bg-grey-main rounded-lg ${ className }`]: true,
               'cursor-pointer': !showDragIcon && !Boolean(state.is_deleted),
               'border border-error ': Boolean(state.is_deleted),
               'hover:bg-hover': !Boolean(state.is_deleted),
            }) }
         >
            <div className='flex gap-3 items-center'>
               <div className='flex w-5 h-5 items-center justify-center'>
                  <Icon
                     name={ state.icon }
                     size='lg'
                     color={ Boolean(state.is_deleted)  ? 'disabled' : isAdded && 'add-new' === leftSideType ? 'action' : 'secondary' }
                  />
               </div>
               <TruncateText
                  text={ state.name || state.title }
                  textClass={ cx({
                     ' text-base leading-[20px] select-none': true,
                     'text-major': !Boolean(state.is_deleted),
                     'text-disabled': Boolean(state.is_deleted),
                  }) }
                  fontWeight='400'
                  textSize='16px'
               />
            </div>
            {
               Boolean(state.is_deleted)  && (
                  <div
                     data-tooltip-id='ams-top-tooltip'
                     data-tooltip-content='Revert'
                     className='h-5'>
                     <Button
                        iconName='revert'
                        fontIconColor='secondary'
                        backgroundColor='transparent'
                        padding='0'
                        textSize='small'
                        classNames='h-full'
                        textMarginX='0'
                        onClick={ e => onClickOptions(e, 'revert') }
                     />
                  </div>
               )
            }
            {
               !Boolean(state.is_deleted)  && (
                  <div className='flex gap-3'>
                     {
                        'add-new' !== leftSideType && item?.has_draft_content && (
                           <div
                              data-tooltip-id='ams-top-tooltip'
                              data-tooltip-content='Unpublished changes'
                              className='flex items-center justify-center w-5 h-5'>
                              <Icon
                                 name='warning'
                                 size='lg'
                                 color='pending'
                              />
                           </div>
                        )
                     }
                     {
                        showDragIcon && !state.is_home_page && (
                           <DragHandle />
                        )
                     }
                     {
                        (!showDragIcon && !withoutLeftIcon) && (
                           <>
                              {
                                 'add-new' !== leftSideType && !state.is_active && (
                                    <div className='flex items-center justify-center w-5 h-5'>
                                       <Icon
                                          name='unpublish'
                                          size='lg'
                                          color='secondary'
                                       />
                                    </div>
                                 )
                              }
                              {
                                 'add-new' !== leftSideType && (
                                    <>
                                       {
                                          state.is_system && !state.is_home_page && !canUpdateOnlyVisibility && (
                                             <>
                                                <div className='h-5'>
                                                   <Button
                                                      text={ state.is_home_page ? 'Edit' : 'View' }
                                                      backgroundColor='transparent'
                                                      primaryColor
                                                      padding='0'
                                                      textSize='small'
                                                      classNames='h-full'
                                                      textMarginX='0'
                                                      onClick={ e => {
                                                      } }
                                                   />

                                                </div>
                                             </>
                                          )
                                       }
                                       {
                                          (!state.is_system || state.is_home_page || canUpdateOnlyVisibility) && (
                                             <>
                                                <ActionButton
                                                   iconName={ GLOBA_SEGMENT_KEY === leftSideType ? 'arrow-right' : 'options-vertical' }
                                                   size='lg'
                                                   data-tooltip-id={ `left-item-${ itemIndex }-settings-tooltip` }
                                                   onClick={ (e) => {
                                                      if([SECTION_SEGMENT_KEY, MENU_SEGMENT_KEY, PAGE_SEGMENT_KEY].includes(leftSideType)){
                                                         optionButtonClick(e, false);
                                                         setOpenSettinsTooltip(!isOpentSettinsTooltip);
                                                      }
                                                   } }
                                                />
                                             </>
                                          )
                                       }

                                    </>

                                 )
                              }
                              {
                                 'add-new' === leftSideType && (
                                    <div className='h-5'>
                                       <Button
                                          text='Add'
                                          backgroundColor='transparent'
                                          primaryColor
                                          padding='0'
                                          textSize='small'
                                          classNames='h-full'
                                          textMarginX='0'
                                          onClick={ _ => {
                                          } }
                                          disabled={ !canAdd }
                                       />

                                    </div>

                                 )
                              }
                              {
                                 createPortal(
                                    <Tooltip
                                       openOnClick
                                       role='dialog'
                                       variant='light'
                                       id={ `left-item-${ itemIndex }-settings-tooltip` }
                                       place='bottom'
                                       clickable
                                       isOpen={ isOpentSettinsTooltip }
                                       offset={ 4 }
                                       noArrow
                                       style={ {
                                          padding: 0,
                                          borderRadius: '8px',
                                       } }
                                       className='shadow-dropdown dark:shadow-dark border dark:border-divider-dark border-divider min-w-[200px] z-60'
                                    >
                                       {
                                          !canUpdateOnlyVisibility && (
                                             <OptionsButton
                                                iconName='pencil'
                                                fontColor='action'
                                                text='Edit'
                                                onClick={ (e) => {
                                                   optionButtonClick(e);
                                                   onClick();
                                                   setOpenSettinsTooltip(false);
                                                } }
                                             />

                                          )
                                       }
                                       {
                                          !state.is_home_page && (
                                             <OptionsButton
                                                iconName={ state.is_active ? 'unpublish' : 'views' }
                                                fontColor='secondary'
                                                text={ state.is_active ? 'Hide' : 'Show' }
                                                onClick={ (e) => onClickOptions(e, 'visibility') }
                                             />
                                          )
                                       }
                                       {
                                          (!state.is_home_page && !canUpdateOnlyVisibility) && (
                                             <OptionsButton
                                                iconName='delete'
                                                fontColor='error'
                                                text={ `Delete ${ leftSideType === MENU_SEGMENT_KEY ? PAGE_SEGMENT_KEY : leftSideType }` }
                                                onClick={ e => onClickOptions(e, 'delete') }

                                             />
                                          )
                                       }

                                    </Tooltip >,
                                    document.getElementById('tooltips')
                                 )
                              }

                           </>
                        )
                     }
                  </div>
               )
            }
         </div>
      </>
   )
}

const AddNewSectionModal = ({
   isMobile,
   states,
   onClose,
   addNewSection,
   currentPage,
}) => {
   const sections = [
      'hero',
      'videos_content_showcase',
      'stories_library',
      'video_library',
      'photoset_library',
      'recorded_livestreams_library',
      'store',
      'memberships',
      'mailing_list',
      'contact',
      'about_me',
   ]
   const [loadingButtonType, setLoadingButtonType] = useState(null)
   return (
      <ConfirmModal
         title='Add new section'
         type='confirm'
         className='!p-6'
         icon='add'
         contentWidth='375px'
         onCloseModal={ onClose }
         isPreventDeafault={ false }
         isMobile={ isMobile }
         withoutCancelButton
         withoutConfirmButton
         withoutCloseButton={ false }
      >
         <div className='flex flex-col w-full mt-4 gap-2'>
            {
               sections.map((key, index) => {
                  const section = states[key] || { name: key };
                  const systemSection = ['header', 'footer'].includes(key);
                  const canAdd = !systemSection;
                  if(!section){
                     return null;
                  }
                  const name = section.name ||  section.section_name
                  // const existAboutMe=section?.section_type==='about_me' &&
                  return (
                     <LeftSideItem
                        itemIndex={ index }
                        key={ index.toString() }
                        item={ { ...section, title: name } }
                        leftSideType='add-new'
                        canAdd={ canAdd && section?.section_type && section?.section_type !== loadingButtonType }
                        onClick={ () => {
                           if(section?.is_global_settings_section){
                              setLoadingButtonType(section.section_type)
                           }
                           addNewSection(key, section?.is_global_settings_section, () => {
                              if(section?.is_global_settings_section){
                                 setLoadingButtonType(null)
                              }
                           })
                        } }
                        isAdded={ systemSection }
                        dataTooltipContent={ canAdd ? null : `The ${ name.toLocaleLowerCase() } cannot be removed` }
                     />
                  )
               })
            }

         </div>
      </ConfirmModal >
   )
}
Header.defaultProps = {
   type: GLOBA_SEGMENT_KEY,

};
ConfirmationModal.propTypes = {
   onClose: PropTypes.func,
   isMobile: PropTypes.bool,
   disabled: PropTypes.bool,
};
Header.propTypes = {
   title: PropTypes.string,
   goToBack: PropTypes.func,
   onSetStep: PropTypes.func,
   onSaveDraft: PropTypes.func,
   stepActiveState: PropTypes.object,
   disabledSaveAsDraft: PropTypes.object,
   description: PropTypes.string,
   type: PropTypes.oneOf([
      PAGE_SEGMENT_KEY, SECTION_SEGMENT_KEY, GLOBA_SEGMENT_KEY, MENU_SEGMENT_KEY, CREATE_PAGE_SEGMENT_KEY,
   ]),
};

LeftSideItem.defaultProps = {
   type: GLOBA_SEGMENT_KEY,
   className: '',
   withoutLeftIcon: false,
   isVisible: true,
   onClick: () => {},
}
LeftSideTabItem.propTypes = {
   text: PropTypes.string,
   iconName: PropTypes.string,
   checked: PropTypes.bool,
   onClick: PropTypes.func,
   name: PropTypes.string,
}
LeftSideTab.propTypes = {
   activeTab: PropTypes.string,
   onClick: PropTypes.func,
}
LeftSideItem.propTypes = {
   onClickDelete: PropTypes.func,
   className: PropTypes.string,
   withoutLeftIcon: PropTypes.bool,
   onClick: PropTypes.func,
   leftSideType: PropTypes.string,
   itemIndex: PropTypes.any,
   canAdd: PropTypes.bool,
   isAdded: PropTypes.bool,
   dataTooltipContent: PropTypes.string,
   canUpdateOnlyVisibility: PropTypes.bool,
   showDragIcon: PropTypes.bool,
   onToggleVisibility: PropTypes.func,
   onRestore: PropTypes.func,
   item: PropTypes.object,
};

ActionButton.defaultProps = {
   size: 'xl',
   primaryColor: false,
   className: 'flex',
   onClick: () => {},
   buttonState: {},
}
ActionButton.propTypes = {
   iconName: PropTypes.string,
   onClick: PropTypes.func,
   className: PropTypes.string,
   fontColor: PropTypes.string,
   buttonState: PropTypes.object,
   primaryColor: PropTypes.bool,
   disabled: PropTypes.bool,
   size: PropTypes.oneOf([
      'xl', 'lg', null,
   ]),
};

OptionsButton.defaultProps = {
   onClick: () => {},
}
OptionsButton.propTypes = {
   iconName: PropTypes.string,
   text: PropTypes.string,
   fontColor: PropTypes.string,
   onClick: PropTypes.func,
   disabled: PropTypes.bool,
   textSize: PropTypes.string,
   padding: PropTypes.string,
   textColor: PropTypes.string,
   tootltip: PropTypes.object,
};
DotsOptions.propTypes = {
   tooltipId: PropTypes.string,
   showRevertButton: PropTypes.bool,
   onClickRevert: PropTypes.bool,
   options: PropTypes.array,
   onClickOptions: PropTypes.func,
};
AddNewSectionModal.propTypes = {
   isMobile: PropTypes.bool,
   states: PropTypes.object,
   onClose: PropTypes.func,
   addNewSection: PropTypes.func,
   currentPage: PropTypes.object,
};
HeaderAddAndReorderButton.propTypes = {
   addButtonText: PropTypes.string,
   isOpenReorder: PropTypes.bool,
   showAddButton: PropTypes.bool,
   disabledDoneButton: PropTypes.bool,
   onClickReorder: PropTypes.func,
   onClickDone: PropTypes.func,
   onClickCancel: PropTypes.func,
   onClickAddNew: PropTypes.func,
};

export {
   Header,
   LeftSideItem,
   AddNewSectionModal,
   LeftSideTab,
   LeftSideItemSortableItem,
   ConfirmationModal,
   ActionButton,
   OptionsButton,
   DotsOptions,
   HeaderAddAndReorderButton,
};
